// src/hooks/notifications/useNotificationMutations.js
import { useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

const apiUrl = process.env.REACT_APP_API_URL || "";

/**
 * Hook to mark notification as read
 */
export const useMarkNotificationAsRead = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation(
    async (notificationId) => {
      const token = await getAccessTokenSilently();

      const { data } = await axios.put(
        `${apiUrl}/api/notifications/${notificationId}/read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return data;
    },
    {
      onSuccess: () => {
        // Invalidate and refetch notifications and unread count
        queryClient.invalidateQueries("notifications");
        queryClient.invalidateQueries("unreadCount");
      },
      onError: (error) => {
        console.error("Failed to mark notification as read:", error);
      },
    }
  );
};

/**
 * Hook to mark all notifications as read
 */
export const useMarkAllNotificationsAsRead = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      const token = await getAccessTokenSilently();

      const { data } = await axios.put(
        `${apiUrl}/api/notifications/mark-all-read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return data;
    },
    {
      onSuccess: () => {
        // Invalidate and refetch notifications and unread count
        queryClient.invalidateQueries("notifications");
        queryClient.invalidateQueries("unreadCount");
      },
      onError: (error) => {
        console.error("Failed to mark all notifications as read:", error);
      },
    }
  );
};

/**
 * Hook to mark multiple notifications as read (bulk operation)
 */
export const useMarkMultipleNotificationsAsRead = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation(
    async (notificationIds) => {
      const token = await getAccessTokenSilently();

      const { data } = await axios.put(
        `${apiUrl}/api/notifications/mark-multiple-read`,
        { notificationIds },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return data;
    },
    {
      onSuccess: () => {
        // Invalidate and refetch notifications and unread count
        queryClient.invalidateQueries("notifications");
        queryClient.invalidateQueries("unreadCount");
      },
      onError: (error) => {
        console.error("Failed to mark multiple notifications as read:", error);
      },
    }
  );
};

/**
 * Hook to delete notification
 */
export const useDeleteNotification = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation(
    async (notificationId) => {
      const token = await getAccessTokenSilently();

      const { data } = await axios.delete(
        `${apiUrl}/api/notifications/${notificationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return data;
    },
    {
      onSuccess: () => {
        // Invalidate and refetch notifications and unread count
        queryClient.invalidateQueries("notifications");
        queryClient.invalidateQueries("unreadCount");
      },
      onError: (error) => {
        console.error("Failed to delete notification:", error);
      },
    }
  );
};

/**
 * Hook to delete multiple notifications (bulk operation)
 */
export const useDeleteMultipleNotifications = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation(
    async (notificationIds) => {
      const token = await getAccessTokenSilently();

      const { data } = await axios.delete(
        `${apiUrl}/api/notifications/bulk-delete`,
        {
          data: { notificationIds },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return data;
    },
    {
      onSuccess: () => {
        // Invalidate and refetch notifications and unread count
        queryClient.invalidateQueries("notifications");
        queryClient.invalidateQueries("unreadCount");
      },
      onError: (error) => {
        console.error("Failed to delete multiple notifications:", error);
      },
    }
  );
};
